import { Box, BoxProps, useMergeRefs } from "@chakra-ui/react";
import React from "react";
import { ZIndex } from "src/constants";
import { AvelaToastMarginBottomDetector } from "src/hooks/useAvelaToast";

type Props = {
  children?: React.ReactNode;
  sticky?: boolean;
  insideTabPanel?: boolean;
} & BoxProps;
export const AdminFormButtons = React.forwardRef<HTMLDivElement, Props>(
  ({ children, sticky = true, insideTabPanel = false, ...boxProps }, ref) => {
    const width = insideTabPanel
      ? "calc(100% + (var(--chakra-space-10) * 2) + (var(--chakra-space-4) * 2))"
      : "calc(100% + (var(--chakra-space-10) * 2))";
    const marginLeft = insideTabPanel
      ? "calc(-1 * var(--chakra-space-10) + (-1 * var(--chakra-space-4)))"
      : "calc(-1 * var(--chakra-space-10))";
    const marginBottom = insideTabPanel
      ? "calc(-1 * var(--chakra-space-10) + (-1 * var(--chakra-space-4)))"
      : "calc(-1 * var(--chakra-space-10))";

    const localRef = React.useRef<HTMLDivElement>(null);
    const combinedRef = useMergeRefs(localRef, ref);

    const [marginTop, setMarginTop] = React.useState(0);

    React.useEffect(() => {
      const current = localRef.current;
      if (!current) return;
      const parent = current.parentElement;
      if (!parent) return;

      /**
       * Workaround for CSS transforms affecting sticky positioning:
       * CSS transforms (e.g., from Popovers) increase scrollHeight without affecting document flow,
       * causing sticky elements to appear misaligned. Use MutationObserver to detect changes
       * and adjust marginTop to compensate for the extra scroll space.
       */
      const originalDelta = parent.scrollHeight - parent.offsetHeight;
      const onResize = () => {
        const delta = parent.scrollHeight - parent.offsetHeight;
        if (delta !== originalDelta) {
          setMarginTop(delta + current.clientHeight);
        } else {
          setMarginTop(0);
        }
      };

      const observerConfig = {
        childList: true, // Watch direct children
        subtree: true, // Watch nested children
      };
      const observer = new MutationObserver(onResize);
      observer.observe(parent, observerConfig);

      return () => {
        observer.disconnect();
      };
    }, []);

    return (
      <AvelaToastMarginBottomDetector elementRef={localRef}>
        <Box
          ref={combinedRef}
          display="flex"
          direction="row"
          gap={3}
          justifyContent="end"
          width={width}
          marginTop={marginTop}
          marginLeft={marginLeft}
          marginBottom={marginBottom}
          paddingX="10"
          paddingY="5"
          bg="white"
          zIndex={ZIndex.AdminFormButtons}
          {...(sticky
            ? {
                shadow: "dark-lg",
                position: "sticky",
                bottom: "calc(-1 * var(--chakra-space-10))",
              }
            : {})}
          {...boxProps}
        >
          {children}
        </Box>
      </AvelaToastMarginBottomDetector>
    );
  }
);
